/* eslint-disable max-lines */
import CartQuery from 'Query/Cart.query';
import { CartDispatcher as SourceCartDispatcher } from 'SourceStore/Cart/Cart.dispatcher';
import { setCartLoadingState } from 'Store/Cart/Cart.action';
import { CART_TOTALS } from 'Store/Cart/Cart.reducer';
import { showNotification } from 'Store/Notification/Notification.action';
import { getAuthorizationToken, isSignedIn } from 'Util/Auth';
import BrowserDatabase from 'Util/BrowserDatabase';
import { getGuestQuoteId, setGuestQuoteId } from 'Util/Cart';
import {
    getFlashSaleCustomerCartQuantityLimit,
    isRunningFlashSale
} from 'Util/FlashSale';
import { addToCartGTMEvent, removeFromCartGTMEvent } from 'Util/GTM/GTM';
import { getExtensionAttributes } from 'Util/Product';
import { fetchMutation, fetchQuery } from 'Util/Request';

export const UpdateOnlineCustomerDispatcher = import(
    /* webpackMode: "lazy", webpackChunkName: "dispatchers" */
    'Store/updateOnlineCustomer/updateOnlineCustomer.dispatcher'
);

/** @namespace AllHomePwafe/Store/Cart/Dispatcher/CartDispatcher */
export class CartDispatcher extends SourceCartDispatcher {
    _syncCartWithBE(dispatch) {
        dispatch(setCartLoadingState(true));

        // Need to get current cart from BE, update cart
        fetchQuery(CartQuery.getCartQuery(
            !isSignedIn() && getGuestQuoteId()
        )).then(
            /** @namespace AllHomePwafe/Store/Cart/Dispatcher/fetchQuery/then */
            (result) => this.handle_syncCartWithBESuccess(dispatch, result),
            /** @namespace AllHomePwafe/Store/Cart/Dispatcher/fetchQuery/then */
            (error) => this.handle_syncCartWithBEError(dispatch, error)
        ).finally(
            /** @namespace AdmScandipwa/Bck/Store/Cart/Dispatcher/CartDispatcher/_syncCartWithBE/then/finally/dispatch */
            () => dispatch(setCartLoadingState(false))
        );
    }

    getProductVariantForAddToCart({ product, productToAdd }) {
        if (!product || !product.variants) {
            return null;
        }
        if (!Array.isArray(productToAdd?.product_option?.extension_attributes?.configurable_item_options)) {
            return null;
        }
        const productConfigOptions = productToAdd?.product_option?.extension_attributes?.configurable_item_options;
        const selectedVariant = product.variants.find((variant) => productConfigOptions.every(
            ({ option_id, option_value }) => Object.values(variant.attributes)
                .find(
                    ({ attribute_id, attribute_value }) => attribute_id === option_id
                        && attribute_value === option_value
                )
        ));

        return selectedVariant.sku;
    }

    getExistingQuantityFromCart(sku) {
        const { items: cartItems = [] } = BrowserDatabase.getItem(CART_TOTALS) || { items: [] };

        const { qty: productQuantityInCart } = cartItems
            .find(({ sku: cartItemSku }) => cartItemSku === sku) || { qty: 0 };

        return productQuantityInCart;
    }

    verifyFlashSaleQuantity({
        product, quantity, dispatch, sku, isUpdate
    }) {
        const isFlashSaleRunning = isRunningFlashSale(product);
        if (isFlashSaleRunning) {
            const flashSaleCustomerCartQuantityLimit = getFlashSaleCustomerCartQuantityLimit();
            const existingProductQuantity = isUpdate ? 0 : this.getExistingQuantityFromCart(product.sku || sku);
            const totalQuantity = quantity + existingProductQuantity;

            if (totalQuantity > flashSaleCustomerCartQuantityLimit) {
                dispatch(showNotification('error',
                    __('Can not add quantity more than user flash sale limit.')));

                return false;
            }
        }

        return true;
    }

    async addProductToCart(dispatch, options) {
        const guestQuoteId = getGuestQuoteId();
        const {
            product,
            quantity,
            currencyCode,
            productOptionsData
        } = options;

        const flashSaleQuantityVerificationState = this.verifyFlashSaleQuantity({ ...options, dispatch });

        if (!flashSaleQuantityVerificationState) {
            return Promise.reject();
        }

        const {
            sku,
            type_id: product_type
        } = product;

        const {
            productOptions,
            productOptionsMulti,
            downloadableLinks
        } = productOptionsData || {};

        // Title removed
        const formatedProductOptions = productOptions
            // eslint-disable-next-line no-unused-vars
            ?.map(({ title, ...otherProductOptions }) => otherProductOptions);

        const productToAdd = {
            sku,
            product_type,
            quantity,
            product_option: {
                extension_attributes: getExtensionAttributes(
                    {
                        ...product,
                        productOptions: formatedProductOptions,
                        productOptionsMulti,
                        downloadableLinks
                    }
                )
            }
        };

        if (!guestQuoteId) {
            await this.createGuestEmptyCart(dispatch);
        }

        if (this._canBeAdded(options)) {
            try {
                const { saveCartItem: { cartData } } = await fetchMutation(CartQuery.getSaveCartItemMutation(
                    productToAdd, !isSignedIn() && getGuestQuoteId()
                ));

                addToCartGTMEvent({
                    product,
                    quantity,
                    currencyCode,
                    sku,
                    variant: this.getProductVariantForAddToCart({ product, productToAdd }),
                    price: (cartData.items.find((record) => record.sku === sku) || {}).price
                });

                const AuthToken = getAuthorizationToken();

                const userOnlineData = {
                    customer_token: AuthToken,
                    cart_id: guestQuoteId
                };

                UpdateOnlineCustomerDispatcher.then(
                    ({ default: dispatcher }) => dispatcher.updateOnlineCustomer(userOnlineData)
                );

                return this._updateCartData(cartData, dispatch);
            } catch ([{ message }]) {
                dispatch(showNotification('error', message));
                return Promise.reject();
            }
        }

        return Promise.reject();
    }

    changeItemQty(dispatch, options, tries = 0) {
        const {
            item_id, quantity, sku, product, currencyCode, oldQuantity = 0
        } = options;

        if (oldQuantity < quantity) {
            const flashSaleQuantityVerificationState = this.verifyFlashSaleQuantity({
                ...options, dispatch, isUpdate: true
            });

            if (!flashSaleQuantityVerificationState) {
                return Promise.reject();
            }
        }

        if (tries > 2) {
            dispatch(showNotification('error', __('Internal server error. Can not add to cart.')));
            return Promise.reject();
        }

        return fetchMutation(CartQuery.getSaveCartItemMutation(
            { sku, item_id, quantity },
            !isSignedIn() && getGuestQuoteId()
        )).then(
            /** @namespace AllHomePwafe/Store/Cart/Dispatcher/fetchMutation/then */
            ({ saveCartItem: { cartData } }) => {
                this._updateCartData(cartData, dispatch);
                if (quantity > oldQuantity) {
                    addToCartGTMEvent({
                        product,
                        quantity,
                        currencyCode,
                        sku: product.sku,
                        variant: sku,
                        price: (cartData.items.find((record) => record.sku === sku) || {}).price
                    });
                } else {
                    removeFromCartGTMEvent({
                        product,
                        quantity,
                        currencyCode,
                        sku: product.sku,
                        variant: sku,
                        price: (cartData.items.find((record) => record.sku === sku) || {}).price
                    });
                }
            },
            /** @namespace AllHomePwafe/Store/Cart/Dispatcher/fetchMutation/then */
            (error) => {
                const [{ debugMessage = '' }] = error || [{}];

                if (debugMessage.match('No such entity with cartId ')) {
                    return this._createEmptyCart(dispatch).then(
                        /** @namespace AllHomePwafe/Store/Cart/Dispatcher/_createEmptyCart/then */
                        (data) => {
                            setGuestQuoteId(data);
                            this._updateCartData({}, dispatch);
                            return this.changeItemQty(dispatch, options, tries + 1);
                        }
                    );
                }

                dispatch(showNotification('error', error[0].message));
                return Promise.reject();
            }
        );
    }

    removeProductFromCart(dispatch, options) {
        const {
            item_id, product, currencyCode, quantity, sku, price
        } = options;

        return fetchMutation(CartQuery.getRemoveCartItemMutation(
            item_id,
            !isSignedIn() && getGuestQuoteId()
        )).then(
            /** @namespace AllHomePwafe/Store/Cart/Dispatcher/fetchMutation/then */
            ({ removeCartItem: { cartData } }) => {
                this._updateCartData(cartData, dispatch);
                removeFromCartGTMEvent({
                    product,
                    sku: product.sku,
                    quantity,
                    currencyCode,
                    variant: sku,
                    price
                });

                return cartData;
            },
            /** @namespace AllHomePwafe/Store/Cart/Dispatcher/fetchMutation/then */
            (error) => {
                dispatch(showNotification('error', error[0].message));
                return null;
            }
        );
    }

    async getQuoteId() {
        const query = CartQuery.getQuoteIdQuery();
        const { customerCart: { id = false } = {} } = await fetchQuery(query);
        return id;
    }

    async fetchAndUpdateCart(dispatch) {
        await fetchQuery(CartQuery.getCartQuery(
            !isSignedIn() && getGuestQuoteId()
        )).then(
            /** @namespace AllHomePwafe/Store/Cart/Dispatcher/fetchQuery/then */
            ({ cartData }) => {
                this._updateCartData(cartData, dispatch);
                return cartData;
            },
            /** @namespace AllHomePwafe/Store/Cart/Dispatcher/fetchQuery/then */
            () => {
                this.onError(dispatch);
                this._updateCartData({}, dispatch);
                return {};
            }
        );
    }

    clearCartSessionVariables() {
        const query = CartQuery.clearCartSessionVariablesQuery();
        fetchQuery(query);
    }

    onError(dispatch) {
        dispatch(showNotification('error', __('Unable get latest cart data from Backend !')));
    }
}

export default new CartDispatcher();
